import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, Subject, finalize, tap } from 'rxjs';

import { KHThemeService } from '.';
import { App, User } from '../models';
import { SNACKBAR_CONSTANTS } from '../shared/constants';
import { AppsDataService } from './data-services';
import { ColorsUtilityService } from './utility-services/colors-utility.service';

@Injectable({
    providedIn: 'root'
})
export class AppsService {
    private platformAppsLoaded = new Subject<boolean>();
    apps: App[] = [];
    currentApp: App;
    app: BehaviorSubject<App> = new BehaviorSubject<App>(null);
    appsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    newApp: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    appAdmins: BehaviorSubject<User[]> = new BehaviorSubject<User[]>(null);
    appAdminLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    loadingData = false;
    isSaving = false;
    platformAppsLoaded$ = this.platformAppsLoaded.asObservable();

    constructor(
        private _snackBar: MatSnackBar,
        private _appsDataService: AppsDataService,
        private _themeService: KHThemeService,
        private _colorsUtlityService: ColorsUtilityService,
        private _router: Router
    ) {
        const app = JSON.parse(sessionStorage.getItem('app'));

        if (app) {
            this.setAppAndTriggerObservable(app);
        }
    }

    init(): void {
        const app = JSON.parse(sessionStorage.getItem('app'));

        if (app) {
            this.setAppAndTriggerObservable(app);
        }
    }

    updateAppInformation(app: App): Observable<App> {
        this.appsLoading.next(true);

        return this._appsDataService.updateAppInformation(app)
            .pipe(
                tap({
                    next: (updatedApp: App) => {
                        this.addUpdateApps(updatedApp);
                        this._snackBar.open(`App Updated`, `Close`, { duration: 5000 });
                    },
                    error: () => {
                        this._snackBar.open(`Unable to update app.`, `Close`, { duration: 5000 });
                    }
                }),
                finalize(() => {
                    this.appsLoading.next(false);
                })
            );
    }

    createApp(app: App): Observable<App> {
        this.appsLoading.next(true);

        return this._appsDataService.createApp(app)
            .pipe(
                tap({
                    next: (createdApp: App) => {
                        this.addUpdateApps(createdApp);
                        this._snackBar.open(SNACKBAR_CONSTANTS.APP_CREATED, SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    },
                    error: () => {
                        this._snackBar.open(SNACKBAR_CONSTANTS.APP_CREATE_ERROR, SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    }
                }),
                finalize(() => {
                    this.appsLoading.next(false);
                })
            );
    }

    addUpdateApps(app: App): void {
        if (this.apps.find((object: App) => object.id === app.id)) {
            const index = this.apps.findIndex((object: App) => object.id === app.id);
            this.apps[index] = app;
        }
        else {
            this.apps.push(app);
        }

        this.setApps(this.apps);
    }

    setAppAndTriggerObservable(app: App): void {
        this.setAppProperties(app);
        this.app.next(app);
    }

    setAppProperties(app): void {
        sessionStorage.setItem('app', JSON.stringify(app));
        this.currentApp = app;
        this._colorsUtlityService.initModelOnAppSelection(app);
        this._colorsUtlityService.setAppColorForSelection(app);
    }

    getApp(): void {
        this.app.next(JSON.parse(sessionStorage.getItem('app')));
    }

    setApps(apps: App[]): void {
        this.apps = apps;
        this.appsLoading.next(false);
        this.platformAppsLoaded.next(true);
    }

    disconnectApp(): void {
        sessionStorage.removeItem('app');
        this.app.next(null);
    }

    loadPlatformApps(): void {
        this._appsDataService.loadPlatformApps()
            .subscribe({
                next: (response: App[]) => {
                    this.setApps(response);
                }
            });
    }

    loadAppFromURL(appUrl: string): void {
        if (this.currentApp?.url === appUrl) {
            return;
        }

        this.loadingData = true;

        this._appsDataService.loadApp(appUrl)
            .subscribe({
                next: (result: App) => {
                    this.setAppAndTriggerObservable(result);
                },
                error: () => {
                    this._router.navigate(['platform', 'user-apps']);
                }
            })
            .add(() => {
                this.loadingData = false;
            });
    }

    updateApp(app: App, formData: FormData): void {
        this.loadingData = true;

        this._appsDataService.updateApp(app, formData)
            .subscribe((response: App) => {
                this._snackBar.open(`${response.name} has been saved`, `Close`, { duration: 3000 });
                this.setAppAndTriggerObservable(response);
                this._themeService.setTheme(response);
            })
            .add(() => {
                this.loadingData = false;
            });
    }

    setMapLayerPreferenceOnApp(app: App): void {
        this.loadingData = true;
        this._appsDataService.setMapLayerPreferenceOnApp(app)
            .subscribe((response: App) => {
                this._snackBar.open(`${response.name} has been saved`, `Close`, { duration: 3000 });
                this.setAppProperties(response);
            })
            .add(() => {
                this.loadingData = false;
            });
    }
}
